/**
 * Unified Chat System
 * Integrates: Models + Tools + Agents + Skills + MCP
 * Like Claude Code but with Ollama + Cloudflare
 */

import { getUnifiedClient } from './unified-model-client.js';
import { MCPToolExecutor, getAnthropicTools } from './mcp-tools.js';
import { AgentManager, detectAgentType } from './agent-system.js';
import { SkillExecutor, parseSkillCommand } from './skills.js';

export class UnifiedChatSystem {
  constructor() {
    this.modelClient = null;
    this.toolExecutor = new MCPToolExecutor();
    this.agentManager = null;
    this.skillExecutor = null;
    this.conversationHistory = [];
    this.activeAgents = [];
  }

  /**
   * Initialize the system
   */
  initialize() {
    try {
      this.modelClient = getUnifiedClient();
      this.agentManager = new AgentManager(this.modelClient, this.toolExecutor);
      this.skillExecutor = new SkillExecutor(this.agentManager);

      console.log('[UnifiedChatSystem] ✅ Initialized');
      console.log('[UnifiedChatSystem] Tools:', getAnthropicTools().length);
      console.log('[UnifiedChatSystem] Skills:', this.skillExecutor.listSkills().length);

      return true;
    } catch (error) {
      console.error('[UnifiedChatSystem] ❌ Initialization failed:', error);
      this.modelClient = null;
      this.agentManager = null;
      this.skillExecutor = null;
      return false;
    }
  }

  /**
   * Check if system is ready
   */
  isReady() {
    return !!(this.modelClient && this.agentManager && this.skillExecutor);
  }

  /**
   * Process user message with full capabilities
   */
  async processMessage(userMessage, options = {}) {
    // Check if system is ready
    if (!this.isReady()) {
      throw new Error('Chat system not ready. Model client not initialized. Check Cloudflare configuration.');
    }

    const {
      model = 'deepseek-v4-pro:cloud',
      temperature = 0.7,
      maxTokens = 4000,
      enableTools = true,
      enableAgents = true,
      enableSkills = true,
      onProgress = null,
      onToolCall = null,
      onAgentSpawn = null
    } = options;

    // Check if it's a skill command
    if (enableSkills) {
      const skillCommand = parseSkillCommand(userMessage);
      if (skillCommand) {
        return await this.executeSkillCommand(skillCommand, {
          onProgress,
          onToolCall,
          onAgentSpawn
        });
      }
    }

    // Check if we should spawn an agent
    if (enableAgents && this.shouldUseAgent(userMessage)) {
      return await this.processWithAgent(userMessage, {
        model,
        temperature,
        maxTokens,
        onProgress,
        onToolCall,
        onAgentSpawn
      });
    }

    // Direct model inference with optional tools
    return await this.processDirectly(userMessage, {
      model,
      temperature,
      maxTokens,
      enableTools,
      onProgress,
      onToolCall
    });
  }

  /**
   * Execute a skill command
   */
  async executeSkillCommand(skillCommand, callbacks = {}) {
    const { onProgress, onToolCall, onAgentSpawn } = callbacks;

    if (onProgress) {
      onProgress(`Executing skill: ${skillCommand.skill.name}`);
    }

    if (onAgentSpawn) {
      onAgentSpawn(skillCommand.skill.agent, skillCommand.skill.description);
    }

    // Wire up callbacks
    const originalRunAgent = this.agentManager.runAgent.bind(this.agentManager);
    this.agentManager.runAgent = async (agentId, message, options) => {
      return await originalRunAgent(agentId, message, {
        ...options,
        onToolCall,
        onThinking: onProgress
      });
    };

    const result = await this.skillExecutor.executeSkill(
      skillCommand.command,
      skillCommand.args
    );

    return {
      type: 'skill_execution',
      skill: skillCommand.skill.name,
      content: this.formatSkillResult(result),
      result,
      agent: skillCommand.skill.agent
    };
  }

  /**
   * Process with agent
   */
  async processWithAgent(userMessage, options = {}) {
    const { onProgress, onToolCall, onAgentSpawn } = options;

    // Detect appropriate agent
    const agentType = detectAgentType(userMessage);

    if (onProgress) {
      onProgress(`Spawning ${agentType} agent...`);
    }

    // Spawn agent
    const agentId = await this.agentManager.spawnAgent(
      agentType,
      userMessage,
      { model: options.model }
    );

    if (onAgentSpawn) {
      const agent = this.agentManager.getAgentStatus(agentId);
      onAgentSpawn(agent.config.name, userMessage);
    }

    this.activeAgents.push(agentId);

    // Run agent
    const result = await this.agentManager.runAgent(agentId, userMessage, {
      ...options,
      enableTools: true,
      onToolCall,
      onThinking: onProgress
    });

    return {
      type: 'agent_execution',
      agent: result.agent,
      content: result.content,
      turns: result.turns,
      toolCalls: result.toolCalls,
      agentId
    };
  }

  /**
   * Process directly with model (no agent)
   */
  async processDirectly(userMessage, options = {}) {
    const {
      model,
      temperature,
      maxTokens,
      enableTools,
      onProgress,
      onToolCall
    } = options;

    // Build conversation context
    const messages = [
      {
        role: 'system',
        content: this.buildSystemPrompt(enableTools)
      },
      ...this.conversationHistory,
      {
        role: 'user',
        content: userMessage
      }
    ];

    let turn = 0;
    const maxTurns = 10;
    let finalResponse = null;
    const toolCallHistory = [];

    while (turn < maxTurns && !finalResponse) {
      turn++;

      if (onProgress) {
        onProgress(`Thinking... (turn ${turn})`);
      }

      // Call model
      const response = await this.modelClient.runInference(
        model,
        messages,
        { temperature, maxTokens }
      );

      if (!response) {
        throw new Error('Model returned no response');
      }

      const responseText = response.content || response.response || '';

      // Check for tool calls
      if (enableTools && this.hasToolCalls(responseText)) {
        const toolCalls = this.extractToolCalls(responseText);

        // Execute tools
        for (const toolCall of toolCalls) {
          if (onToolCall) {
            onToolCall(toolCall.tool, toolCall.parameters);
          }

          const result = await this.toolExecutor.executeTool(
            toolCall.tool,
            toolCall.parameters
          );

          toolCallHistory.push({
            tool: toolCall.tool,
            parameters: toolCall.parameters,
            result
          });

          // Add to conversation
          messages.push({
            role: 'assistant',
            content: responseText
          });

          messages.push({
            role: 'user',
            content: `Tool result:\n${JSON.stringify(result, null, 2)}`
          });
        }

        continue;
      }

      // No tool calls - final response
      finalResponse = {
        type: 'direct_response',
        content: responseText,
        turns: turn,
        toolCalls: toolCallHistory.length,
        model: response.model,
        provider: response.provider
      };
    }

    // Update conversation history
    this.conversationHistory.push({
      role: 'user',
      content: userMessage
    });

    this.conversationHistory.push({
      role: 'assistant',
      content: finalResponse?.content || 'No response generated'
    });

    return {
      ...finalResponse,
      content: finalResponse?.content || 'No response generated'
    };
  }

  /**
   * Build system prompt based on capabilities
   */
  buildSystemPrompt(enableTools) {
    let prompt = `You are an advanced AI development assistant with comprehensive capabilities.

Your configuration from CLAUDE.md:
- 9 DevTeam specialist agents (Frontend, Backend, QA, Architect, etc.)
- GSD framework for project management
- Full tool use capabilities
- Automatic git operations
- Code execution
- Web search and research
`;

    if (enableTools) {
      const tools = getAnthropicTools();
      prompt += `\n\nAvailable Tools (${tools.length}):\n`;
      tools.forEach(tool => {
        prompt += `- ${tool.name}: ${tool.description}\n`;
      });

      prompt += `\nTo use tools, format as:
<tool_use>
{"name": "tool_name", "parameters": {"param": "value"}}
</tool_use>

Or simple format:
TOOL_CALL: tool_name
PARAMETERS: {"param": "value"}
`;
    }

    return prompt;
  }

  /**
   * Check if response has tool calls
   */
  hasToolCalls(text) {
    return text.includes('<tool_use>') ||
           text.includes('TOOL_CALL:') ||
           text.includes('USE_TOOL:');
  }

  /**
   * Extract tool calls
   */
  extractToolCalls(text) {
    // Reuse agent manager's implementation
    return this.agentManager.extractToolCalls(text);
  }

  /**
   * Determine if we should use an agent
   */
  shouldUseAgent(userMessage) {
    const taskPatterns = [
      /\b(build|create|implement|develop|write)\s+(a|an|the)?\s*(component|api|service|feature)/i,
      /\b(fix|debug|investigate|troubleshoot)\b/i,
      /\b(review|audit|check)\s+(code|security|quality)/i,
      /\b(plan|design|architect)/i,
      /\b(test|testing|coverage)/i,
      /\b(document|write docs)/i
    ];

    return taskPatterns.some(pattern => pattern.test(userMessage));
  }

  /**
   * Format skill execution result
   */
  formatSkillResult(result) {
    const { skill, result: skillResult, agent } = result;

    let output = `**${skill} Complete**\n\n`;
    output += `Agent: ${agent}\n\n`;

    if (skillResult.roadmap) {
      output += `## Roadmap\n${skillResult.roadmap.content}\n\n`;
    }

    if (skillResult.plan) {
      output += `## Plan\n${skillResult.plan.content}\n\n`;
    }

    if (skillResult.verification) {
      output += `## Verification\n${skillResult.verification.content}\n\n`;
      output += skillResult.passed ? '✅ PASSED' : '❌ FAILED';
    }

    if (skillResult.nextStep) {
      output += `\n\n**Next:** ${skillResult.nextStep}`;
    }

    return output;
  }

  /**
   * Get active agents
   */
  getActiveAgents() {
    return this.activeAgents.map(id => this.agentManager.getAgentStatus(id));
  }

  /**
   * Clear conversation
   */
  clearConversation() {
    this.conversationHistory = [];
  }

  /**
   * Get conversation history
   */
  getHistory() {
    return this.conversationHistory;
  }
}

/**
 * Singleton instance
 */
let chatSystemInstance = null;

export function initializeUnifiedChatSystem() {
  chatSystemInstance = new UnifiedChatSystem();
  chatSystemInstance.initialize();
  return chatSystemInstance;
}

export function getUnifiedChatSystem() {
  if (!chatSystemInstance) {
    throw new Error('Unified chat system not initialized');
  }
  return chatSystemInstance;
}
